import { TimeLine } from '../generated/graphql'
import { SocketClient } from './socket-client'
import { useCallback, useEffect, useState } from 'react'
// Reexport from here the graphql enum

export enum SocketInternalEvents {
  JOIN_ROOM = 'joinRoom',
  LEAVE_ROOM = 'leaveRoom',
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  ERROR = 'error',
}
export enum SocketEvents {
  Refresh = 'refresh',
  TaskCompleted = 'task-completed',
  TimelineUpdate = 'timeline-update',
}

const socket = SocketClient.get()

/**
 * Returns the shared socket status. Status is updated only by SocketClient (single connect/disconnect/error listener).
 * Multiple hook instances subscribe to the same status stream instead of attaching duplicate socket listeners.
 */
export function useSocketStatus() {
  const [status, setStatus] = useState<'connected' | 'disconnected'>(() =>
    SocketClient.getStatus()
  )
  useEffect(() => {
    return SocketClient.subscribeStatus(setStatus)
  }, [])
  return status
}

/**
 * Room id could be the UUID of the item of the page we are in.
 * This function is a helper that create a hook functions  useSocketRoom(ID)(HookParams)
 */
export function useSocketRoom(roomId: string | undefined) {
  const status = useSocketStatus()
  const socket = SocketClient.get()

  if (!socket) {
    throw new Error('Socket not ready')
  }
  useEffect(() => {
    if (!roomId) {
      return
    }
    if (status) {
      socket.emit(SocketInternalEvents.JOIN_ROOM, roomId)
    }
    return () => {
      socket.emit(SocketInternalEvents.LEAVE_ROOM, roomId)
    }
  }, [status, roomId])
  /**
   * Here we can emit different events linked to that room. event one is generic and accepts a SocketEvent enum
   */
  return {
    /**
     * NOTE: Don't pass a ()=> {} anonymus function as a callback, will reset the listener on every render
     */
    event: function <T = undefined>(
      event: SocketEvents,
      callback: (param?: T) => void
    ) {
      const [previousCallback, setPreviousCallback] = useState<
        (() => void) | undefined
      >()
      useEffect(() => {
        if (!roomId) {
          return
        }
        if (callback === previousCallback) {
          return
        }

        previousCallback && socket.off(event, previousCallback)
        socket.on(event, (param: { roomId: string; payload: T }) => {
          if (param.roomId !== roomId) {
            return
          }
          callback(param.payload)
        })
        setPreviousCallback(callback)
        return () => {
          socket.off(event, callback)
        }
      }, [callback, roomId])
    },
  }
}

export function useSocketTimeline(roomId: string | undefined) {
  const [timeline, setTimeline] = useState<TimeLine[]>([])

  const callback = useCallback((payload: TimeLine | undefined) => {
    if (!payload) {
      return
    }
    setTimeline((prev) => [...prev, payload])
  }, [])

  useEffect(() => {
    if (!roomId) {
      return
    }
    setTimeline([])
  }, [roomId])

  useSocketRoom(roomId).event(SocketEvents.TimelineUpdate, callback)
  return timeline
}
